import { DefaultTemplate } from "components/modal/templates";
import { Datetime, Field, Select } from "components/shared/form";
import Loader from "components/shared/loader";
import { waybillTitleStatusesEnum, waybillTitleTypesEnum } from "helpers/enums";
import { hasValue } from "helpers/system";
import React from "react";
import { namesList } from "sources/books/drivers";
import { applyStatus } from "sources/ebills";

const FIELD_LAYOUT = {
    labelWrapper: "col-sm-4 col-md-4 col-form-label col-form-label-sm text-md-right text-sm-left",
    fieldWrapper: "col-sm-8 col-md-8",
    errorWrapper: "col-sm-8 col-md-8 offset-sm-4 offset-md-4",
};

const DATE_FIELDS = [
    {
        name: "factEnteredAt",
        label: "Фактические дата и время прибытия",
    },
    {
        name: "factLeftAt",
        label: "Фактические дата и время убытия",
    },
];

const TEXT_FIELDS = [
    {
        name: "cargoConditionNotes",
        label: "Замечания по состоянию груза",
        maxLength: 2000,
    },
    {
        name: "cargoWeightNotes",
        label: "Замечания по массе груза",
        maxLength: 1000,
        titleTypes: [waybillTitleTypesEnum.T1],
    },
    {
        name: "cargoPlacesNotes",
        label: "Замечания по количеству грузовых мест",
        maxLength: 1000,
        titleTypes: [waybillTitleTypesEnum.T1],
    },
    {
        name: "note",
        label: "Примечание",
        maxLength: 2000,
    },
];

const getLoadUnloadDateField = (type) => ({
    name: "factLoadUnloadAt",
    label:
        type === waybillTitleTypesEnum.T3
            ? "Фактические дата и время постановки на выгрузку"
            : "Фактические дата и время постановки на погрузку",
});

const getLoadingUnloadingNotesField = (type) => ({
    name: "loadingUnloadingNotes",
    label: type === waybillTitleTypesEnum.T3 ? "Замечания по выгрузке" : "Замечания по погрузке",
    maxLength: 1000,
});

const getFieldsForTitleType = (type) => [
    ...DATE_FIELDS,
    getLoadUnloadDateField(type),
    ...TEXT_FIELDS.filter(({ titleTypes }) => !titleTypes || titleTypes.includes(type)),
    getLoadingUnloadingNotesField(type),
];

const getInitialRemarks = (remarks = {}) =>
    getFieldsForTitleType(waybillTitleTypesEnum.T1).reduce(
        (state, { name }) => ({
            ...state,
            [name]: remarks?.[name] || "",
        }),
        {},
    );

export default class AcceptWaybillTitleModal extends DefaultTemplate {
    state = {
        isLoading: false,
        driverId: this.props.driverId || null,
        ...getInitialRemarks(this.props.waybillTitle?.remarks),
    };

    setValue = ({ name, value }) => {
        this.setState({ [name]: value });
    };

    buildRemarks = () => {
        const { waybillTitle } = this.props;
        const remarks = getFieldsForTitleType(waybillTitle.type).reduce(
            (state, { name }) => (hasValue(this.state[name]) ? { ...state, [name]: this.state[name] } : state),
            {},
        );

        return Object.keys(remarks).length > 0 ? remarks : undefined;
    };

    handleSubmit = (event) => {
        event.preventDefault();

        if (this.state.isLoading) {
            return;
        }

        const { waybillId, callback, waybillTitle } = this.props;
        const remarks = this.buildRemarks();
        const params = {
            waybillId,
            driverId: hasValue(this.state.driverId) ? this.state.driverId : null,
            titles: [
                {
                    id: waybillTitle.id,
                    status: waybillTitleStatusesEnum.Accepted,
                    ...(remarks ? { remarks } : {}),
                },
            ],
        };

        this.setState({ isLoading: true });
        return applyStatus(params).then(
            (res) => {
                if (res) {
                    callback?.();
                    this.props.close();
                } else {
                    this.setState({ isLoading: false });
                }
            },
            () => this.setState({ isLoading: false }),
        );
    };

    renderDriverField = () => (
        <Select
            bWrapper="row form-group"
            fieldWrapper={FIELD_LAYOUT.fieldWrapper}
            filterPropName="fullName"
            inputProps={{ name: "driverId" }}
            label="Водитель-подписант"
            labelWrapper={FIELD_LAYOUT.labelWrapper}
            menuPortalTarget={document.body}
            onChange={this.setValue}
            propName="fullName"
            url={namesList}
            value={this.state.driverId}
        />
    );

    renderDateField = ({ label, name }) => (
        <Datetime
            key={name}
            inPortal
            bWrapper="row form-group"
            wrapper={FIELD_LAYOUT.fieldWrapper}
            labelWrapper={FIELD_LAYOUT.labelWrapper}
            errorWrapper={FIELD_LAYOUT.errorWrapper}
            label={label}
            inputProps={{ name, type: "datetime" }}
            libOptions={{ name, autoComplete: "off" }}
            onChange={this.setValue}
            value={this.state[name]}
        />
    );

    renderTextField = ({ label, maxLength, name }) => (
        <Field
            key={name}
            textarea
            bWrapper="row form-group"
            fieldWrapper={FIELD_LAYOUT.fieldWrapper}
            labelWrapper={FIELD_LAYOUT.labelWrapper}
            errorWrapper={FIELD_LAYOUT.errorWrapper}
            label={label}
            inputProps={{ name, maxLength }}
            onChange={this.setValue}
            value={this.state[name]}
        />
    );

    renderBody = () => {
        if (this.state.isLoading) {
            return (
                <div className="loader-wrap">
                    <Loader prefix="margin-center" />
                </div>
            );
        }

        const fields = getFieldsForTitleType(this.props.waybillTitle.type);

        return (
            <form className="modal-window__form container-fluid" onSubmit={this.handleSubmit}>
                {this.renderDriverField()}
                {fields.map((field) =>
                    field.name.startsWith("fact") ? this.renderDateField(field) : this.renderTextField(field),
                )}
                <div className="modal-window__footer modal-window__footer--right">
                    <div className="btn btn-secondary btn-sm modal-window__footer-action" onClick={this.props.close}>
                        Отменить
                    </div>
                    <input
                        type="submit"
                        className="btn btn-brand btn-sm modal-window__footer-action"
                        value={
                            this.props.waybillTitle.status === waybillTitleStatusesEnum.FailedToSend
                                ? "Повторить отправку замечаний"
                                : "Подтвердить"
                        }
                    />
                </div>
            </form>
        );
    };

    renderFooter = () => null;
}
